import {Injectable, OnDestroy} from '@angular/core';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {ProjectActionPermissionEnum} from '@docma-mm-core/permission/project-action-permission.enum';
import {Plan} from '@docma-mm-core/plan/plan';
import {Structure} from '@docma-mm-core/structure/structure';
import {copy} from '@mydocma/common/src/lib/deep';
import {PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {MapService, Pin, PinEditService, PinIdentifier, PinService, PlanService, SelectionAreaService} from '@mydocma/tile-viewer';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Point} from 'leaflet';
import {BehaviorSubject, filter, first, firstValueFrom, Subscription, take} from 'rxjs';
import {MmFilterService} from '../../../../mm-filter/mm-filter.service';
import {MmProjectLoaderService} from '../../../../mm-project/mm-project-loader.service';
import {MmProjectPermissionsService} from '../../../../mm-project/mm-project-permissions-service';
import {MmFaultChangePageService} from '../../../mm-faults-view/mm-fault-right-pane/mm-fault-change-page.service';
import {MmFaultRightPanePageEnum} from '../../../mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.enum';
import {MmFaultsSplitService} from '../../../mm-faults-view/services/mm-faults-split.service';
import {MmFaultsViewService} from '../../../mm-faults-view/services/mm-faults-view.service';
import {MmPlansFilterService} from '../../services/mm-plans-filter.service';
import {MmPlansManagerService} from '../../services/mm-plans-manager.service';
import {MmPlanViewService} from './mm-plan-view.service';
import {PlanEditModeEnum} from './plan-edit-mode.enum';
import {MmTranslateService} from '../../../../mm-translate.service';
import {DeviceService} from '@mydocma/core/src/lib/device';
import {AppContainerService} from '@mydocma/app-container';
import {MmFaultRightPaneService} from '../../../mm-faults-view/mm-fault-right-pane/mm-fault-right-pane.service';
import {BpPinLocationTargetEnum} from '../../../../bp-common/models/bp-pin';
import {FullscreenService} from '@mydocma/core';

@UntilDestroy()
@Injectable()
export class MmPlanEditService implements OnDestroy {

  public cachedSnapshot: Blob;
  private pinEditService: PinEditService;
  private pinService: PinService;
  private planService: PlanService;
  private planEditModeSubject = new BehaviorSubject<PlanEditModeEnum>(PlanEditModeEnum.None);
  planEditMode$ = this.planEditModeSubject.asObservable();
  private awaitingPlanSelectionForAdd = false;

  private cancelEditSubject = new BehaviorSubject<boolean>(false);
  cancelEdit$ = this.cancelEditSubject.asObservable();

  public isPlanEditInitializingSubject = new BehaviorSubject<boolean>(false);
  isPlanEditInitializing$ = this.isPlanEditInitializingSubject.asObservable();

  private subscription: Subscription;
  private startPinMoving: Pin;

  private permissinEditPin$ = this.permissionService.hasProjectActionPermissions([ProjectActionPermissionEnum.EditPins]);
  private droppedPin: Pin;

  private plansManagerService: MmPlansManagerService;
  private faultsViewService: MmFaultsViewService;
  private changeService: MmFaultChangePageService;
  private planViewService: MmPlanViewService;
  private faultsSplitService: MmFaultsSplitService;
  private selectionAreaService: SelectionAreaService;

  constructor(private readonly dialogService: PromptDialogService,
              private readonly translateSerivce: MmTranslateService,
              private readonly projectLoaderService: MmProjectLoaderService,
              public readonly permissionService: MmProjectPermissionsService,
              public readonly planFilterService: MmPlansFilterService,
              private readonly filterModel: MmFilterService,
              private readonly deviceService: DeviceService,
              public appContainerService: AppContainerService,
              private fullScreenService: FullscreenService,
              readonly rightPaneService: MmFaultRightPaneService) {


  }

  get planEditMode(): PlanEditModeEnum {
    return this.planEditModeSubject.value;
  }

  get isPlanEditInitializing(): boolean {
    return this.isPlanEditInitializingSubject.value;
  }

  attachTo(targetToAttach: any) {
    if (targetToAttach instanceof MmPlansManagerService) {
      this.plansManagerService = targetToAttach;
      this.plansManagerService.selectedPlan$
        .pipe(untilDestroyed(this))
        .subscribe(async selectedPlan => {
          if (this.awaitingPlanSelectionForAdd && selectedPlan) {
            this.setPlanEditMode(PlanEditModeEnum.Edit);
            await this.startPinEdit(PlanEditModeEnum.Edit);
            this.awaitingPlanSelectionForAdd = false;
          }
        });
    } else if (targetToAttach instanceof MmFaultsViewService) {
      this.faultsViewService = targetToAttach;
    } else if (targetToAttach instanceof MmFaultChangePageService) {
      this.changeService = targetToAttach;
    } else if (targetToAttach instanceof MmPlanViewService) {
      this.planViewService = targetToAttach;
    } else if (targetToAttach instanceof MmFaultsSplitService) {
      this.faultsSplitService = targetToAttach;
    }
  }

  public async startNewPinEditFromDrop(dropFault: ViewFault, dropPoint: Point) {

    if (await this.assumeCanDrop(dropFault, this.plansManagerService.selectedPlan)) {
      const pinCoord = this.planService.containerPointToPinCoords(dropPoint);

      const attributeGroupParam = await this.planViewService.getAttributeGroup(dropFault.data?.Attribute?.Id);
      this.droppedPin = {
        id: null, anchor: pinCoord, data: dropFault,
        markerOptions: (attributeGroupParam && attributeGroupParam?.attributeGroup && attributeGroupParam?.blobUrl ? this.planViewService.getPinMarkerOptions(attributeGroupParam?.attributeGroup, attributeGroupParam?.blobUrl) : null)
      };

      this.setPlanEditMode(PlanEditModeEnum.Edit);
      if (!this.deviceService.isTablet()) {
        await this.faultsViewService.openRightPane(dropFault);
      } else {
        await this.faultsViewService.setCurrentFault(dropFault);
      }

      this.changeService.setChanges(MmFaultRightPanePageEnum.plan);

      this.pinEditService.startEdit({
        hideExistingPins: true,
        showSelectionArea: true,
      }, this.droppedPin);

      this.plansManagerService.setPlanFlyoutVisible(false);
    }
  }

  public registerEditService(pinEditService: PinEditService, pinServce: PinService, planService?: MapService, selectionAreaService?: SelectionAreaService): void {
    this.pinEditService = pinEditService;
    this.pinService = pinServce;
    this.selectionAreaService = selectionAreaService;


    this.planService = planService as PlanService;

    this.subscription?.unsubscribe();
    this.subscription = new Subscription();
    this.subscription.add(
      this.pinEditService.onPinMoving$
        .pipe(
          filter(p => this.startPinMoving == null),
          untilDestroyed(this))
        .subscribe(marker => {

            this.startPinMoving = copy(marker.pin);
            console.log(this.startPinMoving);
          }
        ));


    this.subscription.add(
      this.pinEditService.onPinMarkerMoved
        .subscribe(async p => {

          if (this.droppedPin) {
            this.changeService.setChanges(MmFaultRightPanePageEnum.plan);
          } else if (this.planEditModeSubject.value !== PlanEditModeEnum.Edit) {
            this.changeService.setChanges(MmFaultRightPanePageEnum.plan);
          }
        })
    );

    this.subscription.add(
      this.pinEditService.onPinMoved$
        .pipe(
          filter(f => this.planEditModeSubject.value !== PlanEditModeEnum.Edit),
          untilDestroyed(this))
        .subscribe(async movedMarker => {
          const resetPin = () => {
            movedMarker.pin.anchor = this.startPinMoving.anchor;
            if (this.startPinMoving.markerAnchor) {
              movedMarker.pin.markerAnchor = this.startPinMoving.markerAnchor;
            }
            this.pinService.removePin(movedMarker.pin);
            this.pinService.addPin(movedMarker.pin);
            this.pinService.selectPin(movedMarker.pin);
            this.startPinMoving = null;
          };

          if (!!(movedMarker?.pin?.data.isLocked ?? false)) {
            await this.dialogService.showNotificationDialog({
              title: this.translateSerivce.instant('MM.plan.pinCantBeMovedTitleByLocked'),
              content: this.translateSerivce.instant('MM.plan.pinCantBeMoved')
            });
            resetPin();
            return;
          }

          if (movedMarker?.pin?.data?.bpPin?.Locations.find(l => l.Type == BpPinLocationTargetEnum.bim)) {
            await this.dialogService.showNotificationDialog({
              title: this.translateSerivce.instant('MM.plan.pinCantBeMovedTitle'),
              content: this.translateSerivce.instant('MM.plan.pinCantBeMovedContent')
            });
            resetPin();
            return;
          }

          if ((await this.dialogService.showDeleteConfirmationDialog({
            title: this.translateSerivce.instant('MM.plan.pinMoved'),
            subTitle: this.translateSerivce.instant('MM.plan.pinMovedSubTitleContent'),
            content: this.translateSerivce.instant('MM.plan.pinMovedContent')
          }))?.value) {
            const pinOrMarkerMoved = (movedMarker.pin.anchor.x !== this.startPinMoving.anchor.x ||
              movedMarker.pin.anchor.y !== this.startPinMoving.anchor.y ||
              movedMarker.pin.markerAnchor.x !== this.startPinMoving.markerAnchor.x ||
              movedMarker.pin.markerAnchor.y !== this.startPinMoving.markerAnchor.y);

            this.startPinEdit((pinOrMarkerMoved ? PlanEditModeEnum.Edit : PlanEditModeEnum.Clipping), {id: movedMarker.pin.id});
            this.changeService.setChanges(MmFaultRightPanePageEnum.plan);

            if (!this.appContainerService.isMobileView && !this.deviceService.isTablet()) {
              this.faultsViewService.openRightPane(movedMarker.pin.data).then(() => {
                this.setPlanEditMode((pinOrMarkerMoved ? PlanEditModeEnum.Edit : PlanEditModeEnum.Clipping));
              });
            }
          } else {
            (await this.dialogService.showNotificationDialog({
              title: this.translateSerivce.instant('MM.plan.pinResetTitle'),
              content: this.translateSerivce.instant('MM.plan.pinReset')
            }));
            resetPin();
          }
        }));
  }

  public async startPinEdit(planEditMode: PlanEditModeEnum, pinId?: PinIdentifier, planId?: number): Promise<void> {
    let plan: Plan;
    let pin: Pin = null;
    this.setPlanEditMode(planEditMode);

    const loadedPlan = this.planViewService.loadedPlan;
    if (planId) {
      plan = (loadedPlan?.id === planId ? loadedPlan : null) ?? this.plansManagerService.selectedPlan;
    } else {
      plan = loadedPlan ?? this.plansManagerService.selectedPlan;
    }

    if (this.planEditMode !== PlanEditModeEnum.None) {
      if (pinId) {
        pin = copy(this.pinService.pins.find(p => p.id === pinId.id));
        if (!pin) {
          const faultPin = plan?.pins?.find(p => p.fault?.id === pinId.id);
          if (faultPin) {
            pin = copy(await this.planViewService.convertToPin(faultPin));
          }
        }
        if (pin?.readonly) {
          pin.readonly = false;
        }

        if (pin) {
          delete pin.markerOptions;
        }
      }

      await this.faultsSplitService.setRightViewVisble(true);
      await this.plansManagerService.setPlanFlyoutVisible(false);

      if (!this.appContainerService.isMobileView && !this.deviceService.isTablet()) {
        await this.faultsViewService.openRightPane(await firstValueFrom(this.faultsViewService.currentFault), 0);
      }
      else {
        await this.faultsViewService.closeRightPane();
        if (this.deviceService.isTablet()) {
          await this.changeService.setPlanSelectionBackdrop();
        }
      }

      // If user opens plan after selecting attribute, update manually to render correct icon
      const currentFault = await firstValueFrom(this.faultsViewService.currentFault);

      this.pinEditService.startEdit({
        hideExistingPins: true,
        showSelectionArea: true,
        disablePinDragging: planEditMode === PlanEditModeEnum.Clipping
      }, pin);
      // fullscreen issue related to  MMW-2668
      const fullScreenState = !!this.fullScreenService?.fullscreenElement && this.appContainerService.isMobileView;
      if (fullScreenState) {
        this.fullScreenService.exitFullscreen();

        this.planEditMode$.pipe(untilDestroyed(this), filter(value => value === PlanEditModeEnum.None),
          take(1)
        ).subscribe(value => {
          const elem = document.querySelector('.plans-view-container') as HTMLElement;
          this.fullScreenService.requestFullscreen(elem);
        });
      }

      // setTimout was  here, which interacting with this.planViewService.latestAttribute , after 100 ms, as it was in setTimout latestAttribute is already null
      // if (pin) {
      //   this.planViewService.updatePin(currentFault, this.planViewService.latestAttribute);
      // }


    }
  }

  public async saveEditPin(): Promise<{ pin: Pin; snapshot: Blob }> {
    const blob = this.cachedSnapshot;
    this.setPlanEditMode(PlanEditModeEnum.None);
    this.startPinMoving = null;
    this.droppedPin = null;
    this.planViewService.latestAttribute = null;

    this.isPlanEditInitializingSubject.next(false);

    const result = await this.pinEditService.stopEdit(false, !blob);

    if (blob) {
      result.snapshot = blob;
    }
    return result;
  }

  public async closeEditPin(): Promise<void> {
    if (this.startPinMoving && (this.planEditMode !== PlanEditModeEnum.None)) {
      const fault = await firstValueFrom(this.faultsViewService.currentFault.pipe(first()));
      const existingPin = this.pinService.pins.find(pin => fault.id === pin.id);
      if (existingPin) {
        existingPin.anchor = this.startPinMoving.anchor;
        if (this.startPinMoving?.markerAnchor) {
          existingPin.markerAnchor = this.startPinMoving.markerAnchor;
        }

        this.pinService.removePin({id: existingPin.id});
        this.pinService.addPin(existingPin);
        this.pinService.selectPin({id: fault.id});
        fault.pin = {
          FileId: fault.pin.FileId,
          PosX: existingPin.anchor.x * 100,
          PosY: existingPin.anchor.y * 100,
          RelativeAnchorToParentPosX: existingPin?.markerAnchor?.x,
          RelativeAnchorToParentPosY: existingPin?.markerAnchor?.y
        };
      }
    }
    this.startPinMoving = null;
    this.droppedPin = null;
    this.pinEditService.stopEdit(true);
    this.planViewService.latestAttribute = null;
    this.setPlanEditMode(PlanEditModeEnum.None);
    this.awaitingPlanSelectionForAdd = false;

    this.isPlanEditInitializingSubject.next(false);
  }

  addPin(pin: Pin): void {
    this.pinService.addPin(pin);
  }

  setPlanEditMode(mode: PlanEditModeEnum): void {
    this.cachedSnapshot = null;
    if (this.planEditModeSubject.value !== mode) {
      this.planEditModeSubject.next(mode);
    }

    if (mode === PlanEditModeEnum.None && this.isPlanEditInitializingSubject.value) {
      console.warn('[setPlanEditMode] Clearing isPlanEditInitializing flag as mode set to None');
      this.isPlanEditInitializingSubject.next(false);
    }
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public isPlanFlyoutVisible(): boolean {
    try {
      return !!(this.plansManagerService && this.plansManagerService.showPlanFlyout);
    } catch {
      return false;
    }
  }

  // public async setNewPinSelected(faultUid: string) {
  //   const pinService = this.planViewService.tileViewerEvent.pinService;
  //
  //   if (this.appContainerService.isMobileView) {
  //     const selectedPins = await firstValueFrom(pinService.selectedPins$);
  //     selectedPins.forEach(p => {
  //       pinService.deselectPin({id: p.id}, false);
  //     });
  //   }
  //
  //   console.warn(pinService.pins);
  //
  //   pinService.selectPin({id: faultUid});
  // }

  public async addSyncedPinOnSelectedPlan(faultLoadState: any, faultUid: string) {
    const pin = this.pinService?.pins?.find(p => p.id === faultUid);
    const selectedPlan = this.plansManagerService.selectedPlan;

    if (pin && selectedPlan?.id === faultLoadState?.data?.PlanId) {
      // this.pinService.removePin({id: faultUid});
      // this.pinService.addPin({
      //   id: faultLoadState?.data?.Id,
      //   anchor: {x: pin.anchor.x, y: pin.anchor.y},
      //   popupComponent: (!this.deviceService.isMobil() && !this.deviceService.isTablet() ? this.faultsViewService.popupComponent : undefined),
      //   data: pin.data,
      //   readonly: !(await firstValueFrom(this.permissinEditPin$.pipe(first()))) || this.appContainerService.isMobileView    // || pin.data.isLocked
      // });

      if (this.appContainerService.isMobileView) {
        const selectedPins = await firstValueFrom(this.pinService.selectedPins$);
        selectedPins.forEach(p => this.pinService.deselectPin({id: p.id}, false));
      }

      this.pinService.selectPin({id: faultLoadState?.data?.Id});
    }
  }

  public async attachPinOnFault(fault: ViewFault, pin: Pin, plan: Plan): Promise<void> {
    const existingPin = this.pinService.pins.find(p => p.id === pin.id);

    const existingFaultPin = plan.pins.find(f => f.fault.id === fault?.id);

    if (existingPin) {
      existingPin.anchor = {x: pin.anchor.x, y: pin.anchor.y};
      // existingPin.readonly = !(await firstValueFrom(this.permissinEditPin$.pipe(first()))) || this.appContainerService.isMobileView || fault.isLocked;
      this.pinService.removePin({id: existingPin.id});
      this.pinService.addPin(existingPin);
    } else {
      this.pinService.addPin({
        id: fault.uid,
        anchor: {x: pin.anchor.x, y: pin.anchor.y},
        popupComponent: (!this.deviceService.isMobil() && !this.deviceService.isTablet() ? this.faultsViewService.popupComponent : undefined),
        data: fault,
        readonly: !(await firstValueFrom(this.permissinEditPin$.pipe(first()))) || this.appContainerService.isMobileView    // || fault.isLocked
      });
    }
    this.pinService.selectPin({id: fault.identifier});
    fault.pin = {
      FileId: plan.id,
      PosX: pin.anchor.x * 100,
      PosY: pin.anchor.y * 100,
    };
    if (!existingFaultPin || existingFaultPin.bpPin ) {
      // MMW-3002: support dragged pin marker only for bp pin
        fault.pin.RelativeAnchorToParentPosX = pin?.markerAnchor?.x;
        fault.pin.RelativeAnchorToParentPosY = pin?.markerAnchor?.y;
    }
  }

  public async addFaultOnPin(): Promise<void> {
    if (this.faultsSplitService.toggledRightViews.length === 0) {
      this.plansManagerService.setPlanFlyoutVisible(true);
      //  this.plansManagerService.setSelectedPlan(null);
      this.faultsSplitService.setRightViewVisble(true);
      // this.awaitingPlanSelectionForAdd = true;
      // await this.changeService.setPlanSelectionBackdrop();
      // return;
    }

    this.isPlanEditInitializingSubject.next(true);
    this.setPlanEditMode(PlanEditModeEnum.Edit);
    this.changeService.setChanges(MmFaultRightPanePageEnum.plan);
    this.startPinEdit(PlanEditModeEnum.Edit);

    if ((this.appContainerService.isMobileView || this.deviceService.isTablet()) && this.planEditMode !== PlanEditModeEnum.None) {
      this.rightPaneService.closePane();
      if (this.deviceService.isTablet()) {
        await this.changeService.setPlanSelectionBackdrop();
      }
    } else if (!this.appContainerService.isMobileView && this.planEditMode !== PlanEditModeEnum.None) {
      this.faultsViewService.openRightPane(await firstValueFrom(this.faultsViewService.currentFault), 0);
    }
  }

  public async filterPlansEditList(struct?: Structure) {
    return await this.planFilterService.filterPlansByFaultStructure(struct || this.filterModel.filterState.structure, this.plansManagerService.allPlans.filter(x => !x.data.BpPlan || x.data.BpPlan?.isDisabled === false));
  }

  public async centerEditPin() {
    if (this.planEditMode === PlanEditModeEnum.Edit) {
      const planService = (this.pinEditService.mapService as PlanService);
      const center = planService.map.getCenter();

      await this.startPinEdit(PlanEditModeEnum.Edit);

      const selectionAreaService = this.pinEditService.selectionAreaService.getSelectionArea();

      const point = planService.geoMapToPinSystemCoordinates(center);
      this.selectionAreaService.getSelectionAreaMarker().setLatLng(center);
      selectionAreaService.transformByCoordinates = center;
      const pin = this.pinEditService.editPin;
      pin.anchor = {x: point.x, y: point.y};
      this.pinEditService.updateEditPin(pin);

      if (this.appContainerService.isMobileView) {
        this.rightPaneService.closePane();
      }
    }
  }

  private async assumeCanDrop(fault: ViewFault, plan: Plan): Promise<boolean> {
    if (!fault.pin) {
      if (!await this.IsFaultStructureValidForPlan(fault, plan)) {
        this.dialogService.showNotificationDialog({
          title: this.translateSerivce.instant('MM.plan.error.pinNotPossible'),
          subTitle: this.translateSerivce.instant('MM.plan.error.faultPlanStructureInvalid'),
          content: this.translateSerivce.instant('MM.plan.error.selectAnotherPlan'),
        });
        return false;
      }
    } else {
      if (plan.id === fault.pin.FileId) {
        this.dialogService.showNotificationDialog({
          title: this.translateSerivce.instant('MM.plan.error.pinNotPossible'),
          subTitle: this.translateSerivce.instant('MM.plan.error.faultPinnedOnPlan'),
        });
        return false;
      } else {
        this.dialogService.showNotificationDialog({
          title: this.translateSerivce.instant('MM.plan.error.pinNotPossible'),
          subTitle: this.translateSerivce.instant('MM.plan.error.faultPinnedOnAnotherPlan'),
        });
        return false;
      }
    }
    return true;
  }

  private async IsFaultStructureValidForPlan(fault: ViewFault, plan: Plan) {
    /// waiting until fault loaded
    await firstValueFrom(fault.loadState$.pipe(filter(ls => ls.status === LoadStatusEnum.Loaded)));
    const structures = await firstValueFrom(this.projectLoaderService.structures.pipe(filter(struct => struct.data.length > 0)));
    const root = structures.data[0];
    return !!root.findById(plan.data.Structure.Id)?.findById(fault.data.Structure.Id);
  }

}
